import { useMutation, useQuery } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { getEnvFromUrl } from '@newfront-insurance/shared-public-config';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import type { SharedInbox } from '../components/chat-v2/components/outlook-mailbox-settings';
import { GET_NOVA_SETTINGS, UPDATE_NOVA_SETTINGS } from '../queries/mutations';
import { msGraphManager } from '../services/ms-graph-manager';

export interface NovaSettingsData {
  notificationsEnabled: boolean;
  outlookConnectEnabled: boolean;
  outlookConnectedEmailGroupIds: string[];
  outlookSharedInboxes: string[];
}

export type NovaSettingField = keyof NovaSettingsData;

export interface UseNovaSettingsResult {
  /** Server settings data (null if not loaded yet) */
  serverSettings: NovaSettingsData | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** GraphQL query error, if any */
  error?: Error;
  /** Persist a single setting to the server */
  persistSetting: (field: NovaSettingField, value: unknown) => void;
  /** Persist multiple settings to the server in a single mutation */
  persistSettings: (fields: Partial<NovaSettingsData>) => void;
}

interface NovaSettingsResponse {
  notificationsEnabled: boolean;
  outlookConnectEnabled: boolean;
  outlookConnectedEmailGroupIds: string[];
  outlookSharedInboxes: string[];
}

// TODO: Remove these localStorage helpers after all users have been migrated (~ Q3 2026).
const LEGACY_NOTIFICATIONS_KEY = 'nova-chat-notifications-enabled';
const LEGACY_OUTLOOK_KEY = 'nova-outlook-connect-enabled';
function getLegacyChatSettingsKey(): string {
  return `${getEnvFromUrl()}-outlook-chat-settings`;
}

/**
 * Clears all legacy localStorage keys after successful migration to the server.
 */
function clearLocalStorageSettings(): void {
  try {
    localStorage.removeItem(LEGACY_NOTIFICATIONS_KEY);
    localStorage.removeItem(LEGACY_OUTLOOK_KEY);
    localStorage.removeItem(getLegacyChatSettingsKey());
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Reads existing settings from localStorage and returns them for migration.
 * This allows existing users to have their settings migrated to the server.
 */
function readLocalStorageSettings(): Partial<NovaSettingsData> | null {
  const result: Partial<NovaSettingsData> = {};
  let hasAny = false;

  try {
    const notificationsStored = localStorage.getItem(LEGACY_NOTIFICATIONS_KEY);
    if (notificationsStored != null) {
      result.notificationsEnabled = notificationsStored === 'true';
      hasAny = true;
    }

    const outlookStored = localStorage.getItem(LEGACY_OUTLOOK_KEY);
    if (outlookStored != null) {
      result.outlookConnectEnabled = outlookStored === 'true';
      hasAny = true;
    }

    const chatSettingsStored = localStorage.getItem(getLegacyChatSettingsKey());
    if (chatSettingsStored) {
      const parsed = JSON.parse(chatSettingsStored) as {
        selectedGroupIds?: string[];
        sharedInboxes?: SharedInbox[];
      };
      if (parsed.selectedGroupIds?.length) {
        result.outlookConnectedEmailGroupIds = parsed.selectedGroupIds;
        hasAny = true;
      }
      if (parsed.sharedInboxes?.length) {
        result.outlookSharedInboxes = parsed.sharedInboxes.map((inbox) => inbox.email);
        hasAny = true;
      }
    }
  } catch {
    // Ignore localStorage errors
  }

  return hasAny ? result : null;
}

export function useNovaSettings(userUuid: string | undefined): UseNovaSettingsResult {
  const hasMigrated = useRef(false);

  const {
    data,
    loading,
    error: queryError,
  } = useQuery(GET_NOVA_SETTINGS, {
    variables: { userUuid: userUuid! },
    skip: !userUuid,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [updateSettings] = useMutation(UPDATE_NOVA_SETTINGS);

  const getNovaSettings = (data as { getNovaSettings?: NovaSettingsResponse } | undefined)?.getNovaSettings;

  const serverSettings: NovaSettingsData | null = useMemo(
    () =>
      getNovaSettings
        ? {
            notificationsEnabled: getNovaSettings.notificationsEnabled,
            outlookConnectEnabled: getNovaSettings.outlookConnectEnabled,
            outlookConnectedEmailGroupIds: getNovaSettings.outlookConnectedEmailGroupIds,
            outlookSharedInboxes: getNovaSettings.outlookSharedInboxes,
          }
        : null,
    [getNovaSettings],
  );

  // Use a ref so persistSetting doesn't depend on serverSettings and stays referentially stable.
  // This prevents context re-renders that would abort active streams.
  const serverSettingsRef: MutableRefObject<NovaSettingsData | null> = useRef(serverSettings);
  useEffect(() => {
    serverSettingsRef.current = serverSettings;
  }, [serverSettings]);

  const persistSetting = useCallback(
    (field: NovaSettingField, value: unknown) => {
      if (!userUuid) return;

      browserLogger.info('[NovaSettings] Persisting setting', { field, value });

      const current = serverSettingsRef.current;
      updateSettings({
        variables: {
          userUuid,
          [field]: value,
        },
        update(cache, { data: mutationData }) {
          if (!mutationData?.updateNovaSettings) return;
          cache.writeQuery({
            query: GET_NOVA_SETTINGS,
            variables: { userUuid },
            data: { getNovaSettings: mutationData.updateNovaSettings },
          });
        },
        optimisticResponse: {
          updateNovaSettings: {
            notificationsEnabled: current?.notificationsEnabled ?? false,
            outlookConnectEnabled: current?.outlookConnectEnabled ?? false,
            outlookConnectedEmailGroupIds: current?.outlookConnectedEmailGroupIds ?? [],
            outlookSharedInboxes: current?.outlookSharedInboxes ?? [],
            [field]: value,
          },
        },
      }).catch((error) => {
        browserLogger.error('[NovaSettings] Failed to persist setting', { field, value, error });
      });
    },
    [userUuid, updateSettings],
  );

  const persistSettings = useCallback(
    (fields: Partial<NovaSettingsData>) => {
      if (!userUuid) return;

      browserLogger.info('[NovaSettings] Persisting multiple settings', { fields });

      const current = serverSettingsRef.current;
      updateSettings({
        variables: {
          userUuid,
          ...fields,
        },
        update(cache, { data: mutationData }) {
          if (!mutationData?.updateNovaSettings) return;
          cache.writeQuery({
            query: GET_NOVA_SETTINGS,
            variables: { userUuid },
            data: { getNovaSettings: mutationData.updateNovaSettings },
          });
        },
        optimisticResponse: {
          updateNovaSettings: {
            notificationsEnabled: current?.notificationsEnabled ?? false,
            outlookConnectEnabled: current?.outlookConnectEnabled ?? false,
            outlookConnectedEmailGroupIds: current?.outlookConnectedEmailGroupIds ?? [],
            outlookSharedInboxes: current?.outlookSharedInboxes ?? [],
            ...fields,
          },
        },
      }).catch((error) => {
        browserLogger.error('[NovaSettings] Failed to persist settings', { fields, error });
      });
    },
    [userUuid, updateSettings],
  );

  // TODO: Remove this one-time migration after all users have been migrated (~ Q3 2026).
  // Reads legacy localStorage keys, pushes them to the server, then clears localStorage.
  useEffect(() => {
    if (!serverSettings || !userUuid || hasMigrated.current) return;

    const isServerEmpty =
      !serverSettings.notificationsEnabled &&
      !serverSettings.outlookConnectEnabled &&
      serverSettings.outlookConnectedEmailGroupIds.length === 0 &&
      serverSettings.outlookSharedInboxes.length === 0;

    if (!isServerEmpty) {
      hasMigrated.current = true;
      return;
    }

    const localSettings = readLocalStorageSettings();
    if (!localSettings) {
      hasMigrated.current = true;
      return;
    }

    hasMigrated.current = true;
    browserLogger.info('[NovaSettings] Migrating localStorage settings to server', localSettings);

    updateSettings({
      variables: {
        userUuid,
        ...(localSettings.notificationsEnabled != null
          ? { notificationsEnabled: localSettings.notificationsEnabled }
          : {}),
        ...(localSettings.outlookConnectEnabled != null
          ? { outlookConnectEnabled: localSettings.outlookConnectEnabled }
          : {}),
        ...(localSettings.outlookConnectedEmailGroupIds
          ? { outlookConnectedEmailGroupIds: localSettings.outlookConnectedEmailGroupIds }
          : {}),
        ...(localSettings.outlookSharedInboxes
          ? { outlookSharedInboxes: localSettings.outlookSharedInboxes as string[] }
          : {}),
      },
    })
      .then(() => {
        clearLocalStorageSettings();
        browserLogger.info('[NovaSettings] Migration complete, localStorage cleared');
      })
      .catch((error) => {
        browserLogger.error('[NovaSettings] Failed to migrate localStorage settings', { error });
      });
  }, [serverSettings, userUuid, updateSettings]);

  // Sync in-memory manager state from server settings.
  // (Browser notifications read the server setting live at display time and
  // need no sync here.)
  useEffect(() => {
    if (!serverSettings) return;

    if (serverSettings.outlookConnectEnabled) {
      msGraphManager.enable();
    } else {
      msGraphManager.disable();
    }
  }, [serverSettings]);

  return {
    serverSettings,
    isLoading: loading,
    error: queryError,
    persistSetting,
    persistSettings,
  };
}
